import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';

import FitType from '@features/blockSuggestion/components/FitType';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { isValidLink } from '@utils/Util';

const FileUploads = ({ id, originFrom = 'FILE', onSetActiveTool, onPlaceImageOnArtboard }) => {
	const [isLinkClicked, setIsLinkClicked] = useState(false);
	const [fileTypes, setFileTypes] = useState([]);
	const [selectedFileType, setSelectedFileType] = useState(null);
	const [fileList, setFileList] = useState({});
	const [link, setLink] = useState('');
	const [inValidLink, setInValidLink] = useState(false);
	const [isError, setIsError] = useState(false);
	const dispatch = useDispatch();
	const [isShowHistory, setIsShowHistory] = useState(false);
	const toolTipRef = useRef();
	const [remark, setRemark] = useState('');

	useEffect(() => {
		if (id) {
			fetchProductFile();
			fetchEnumValues('info.nitex.app.common.enums.ProductFileType?sorted=true', setFileTypes);
		}
	}, [id]);

	const fetchProductFile = async () => {
		dispatch(openLoader());
		try {
			const { data } = await ApiClient.GET('fetchProductFile', id);
			setFileList(data);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onUpdateData = async (item, type) => {
		try {
			dispatch(openLoader());
			let body = {};
			if (type === 'remarks') body.remarks = remark;
			else body.isFinal = true;
			await ApiClient.PUT('uploadProductFile', body, `/${item.id}`);
			fetchProductFile();
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onRemove = async (item) => {
		try {
			await ApiClient.DELETE('uploadProductFile', {}, `/${item.id}`);
			await fetchProductFile();
		} catch (error) {
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const onLinkSave = () => {
		if (isValidLink(link)) {
			const requestBody = new FormData();
			requestBody.append(
				'request',
				JSON.stringify({
					isLink: true,
					link: link,
					fileType: selectedFileType.id,
					productId: id
				})
			);
			onFileUpload(requestBody);
		} else {
			setInValidLink(true);
		}
	};

	const onUploadFile = (e, newlyUploadedFileType = null) => {
		const files = Array.from(e.target.files);
		const formData = new FormData();
		const documentDTOList = {
			fileType: newlyUploadedFileType || selectedFileType.id,
			productId: id,
			documentDTOList: []
		};

		files.forEach((file) => {
			const data = {
				name: file.name,
				docMimeType: file.type,
				print: true,
				documentType: 'PRODUCT_NOT_IMAGE'
			};
			documentDTOList.documentDTOList.push(data);
		});
		files.forEach((file) => {
			formData.append('productFiles', file);
		});
		formData.append('request', JSON.stringify(documentDTOList));

		onFileUpload(formData);
	};

	const onFileUpload = async (fileData) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.UPLOAD_MULTIPLE_FILE_WITH_BODY('uploadProductFileNew', fileData);
			toastSuccess(data.message || 'File uploaded successfully.');
			fetchProductFile();
			setIsLinkClicked(false);
			setInValidLink(false);
			setSelectedFileType(null);
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const onChangeInput = (e) => {
		setRemark(e.target.value);
	};

	const sortAndFilterFiles = (files) => {
		const filesData = files
			.filter((file) => {
				if (!isShowHistory) {
					return !file?.deletedAt;
				} else {
					return file?.deletedAt;
				}
			})
			.sort((a, b) => {
				if (a.isFinal && !b.isFinal) {
					return -1;
				}
				if (!a.isFinal && b.isFinal) {
					return 1;
				}
				return 0;
			});
		return filesData;
	};

	const renderFitTypes = (files) => {
		return (
			files?.length > 0 &&
			sortAndFilterFiles(files).map((item, index) => (
				<div className="all-uploaded-files mt-12" key={item.id}>
					<FitType
						item={item}
						onChangeInput={(e) => onChangeInput(e, item)}
						onUpdateData={(type) => onUpdateData(item, type, files[0])}
						onImageUpload={(e) => {
							onUploadFile(e, item?.fileType?.constant);
						}}
						onRemove={() => onRemove(item, files[0])}
						originFrom={originFrom}
						onSetActiveTool={onSetActiveTool}
						index={index + 1}
						onPlaceImageOnArtboard={onPlaceImageOnArtboard}
					/>
				</div>
			))
		);
	};

	const handleOutsideClick = (e) => {
		if (isError && !toolTipRef?.current?.contains(e.target)) {
			setIsError(false);
		}
	};

	useEffect(() => {
		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
	}, [isError]);

	const renderFileUpload = () => {
		return (
			<div className="upload-and-link">
				{originFrom === 'FILE' && (
					<>
						<div>
							<CustomDropdown
								type="addItem"
								title={'File type'}
								isShowLabel={false}
								onItemClick={(value) => {
									setSelectedFileType(value);
									setIsError(false);
								}}
								items={fileTypes?.map((item) => ({ id: item.id, value: item.label }))}
								selectedItem={selectedFileType?.value}
							/>
						</div>
						<div className="d-flex align-items-center mt-2">
							<div className="text-view-uploader upload-template w-100">
								<label
									htmlFor="templateUpload"
									className="drag-upload cursor-pointer"
									onClick={() => {
										if (!selectedFileType?.value) {
											setIsError(true);
										}
									}}
								>
									<span className="custom-tooltip">
										<p className="regular-14 gray_dark_01">Upload </p>
										{isError && (
											<span className="tooltiptext" ref={toolTipRef}>
												First select file type
											</span>
										)}
									</span>
								</label>
								<input
									type="file"
									className="file-upload d-none"
									name="templateUpload"
									id="templateUpload"
									accept=".pdf, .xls, .xlsx, .xlsb, .doc, .docx, .psd, .ai, .dxf, .zip, .eps"
									onClick={(e) => (e.target.value = null)}
									onChange={(e) => onUploadFile(e)}
									disabled={!selectedFileType?.value}
									multiple
								/>
							</div>
							<p className="regular-12 gray_dark_03 pr-6 pl-6">or</p>
							<button
								className="link-btn regular-12 gray_dark_02 cursor-pointer"
								onClick={() => {
									if (!selectedFileType?.value) {
										setIsError(true);
									} else setIsLinkClicked(true);
								}}
							>
								Link
							</button>
						</div>

						{isLinkClicked && (
							<div className="link-paste d-flex align-items-center mt-2">
								<input
									type="text"
									autoComplete="off"
									placeholder="Link paste here"
									onChange={(e) => setLink(e.target.value)}
								/>
								<button className="close-url" onClick={() => setIsLinkClicked(false)}>
									<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt />
								</button>
								<button className="button size32 save" onClick={onLinkSave}>
									<img src={`${process.env.PUBLIC_URL}/icons/tick.png`} alt />
								</button>
							</div>
						)}
						{inValidLink && <span className="regular-12 red">Invalid link</span>}
					</>
				)}
				<div className="all-files-and-history position-relative mt-3 pt-1">
					{!isShowHistory ? (
						<p
							className="semibold-12 gray_dark_01 text-underline cursor-pointer history-button"
							onClick={() => setIsShowHistory(true)}
						>
							History
						</p>
					) : (
						<p
							className="regular-12 weight-700 text-uppercase d-flex align-items-center cursor-pointer mb-3"
							onClick={() => setIsShowHistory(false)}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
								alt="back"
								className="mr-1"
							/>
							History
						</p>
					)}
					{Object.keys(fileList).length > 0 ? (
						Object.entries(fileList).map(([category, files]) => (
							<div key={category}>
								<h3 className="type-title regular-12 weight-700 gray_dark_02 mb-0">
									{sortAndFilterFiles(files).length > 0 && category.toUpperCase()}
								</h3>
								{renderFitTypes(files)}
							</div>
						))
					) : (
						<div className="d-flex pt-5 mt-5 gray_dark_03 justify-content-center align-content-center">
							<span className="regular-14  gray-1">
								{isShowHistory ? 'No history found' : 'No file uploaded'}
							</span>
						</div>
					)}
				</div>
			</div>
		);
	};

	return <div>{renderFileUpload()}</div>;
};

export default FileUploads;
